/** @jsxImportSource @emotion/react */

import { Space, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';

import { Select } from 'components';
import { $Object } from 'types';
import { perPageList } from 'utils';
import { ChevronLeft, ChevronRight } from 'icons';
import { ThemeContext } from 'contexts';

import { stylesInit } from './pagination.styles';
import styled from '@emotion/styled';

export interface PaginationProps {
  pageSize: number;
  currentPage?: number;
  nextCursor?: string | null;
  previousCursor?: string | null;
  onChange: (val: $Object) => void;
}

export const Pagination = ({ pageSize = 8, currentPage = 1, nextCursor, previousCursor, onChange }: PaginationProps) => {
  const { t } = useTranslation();
  const { currentTheme } = useContext(ThemeContext);

  const styles = stylesInit(currentTheme);

  const StyledButton = styled(Button)`
    background-color: ${currentTheme['grey-70']};
    border: none;
    color: ${currentTheme['grey-40']};
    
    &:hover:not(:disabled) {
      background-color: ${currentTheme['grey-60']} !important;
      color: ${currentTheme['grey-white']} !important;
    }
    
    &:disabled {
      background-color: ${currentTheme['grey-80']};
      color: ${currentTheme['grey-60']};
      opacity: 0.5;
    }
  `;

  const StyledSelect = styled(Select)`
    .ant-select-selector {
      background-color: ${currentTheme['grey-70']} !important;
    }

    &.ant-select-open {
      .ant-select-selection-item {
        color: ${currentTheme['grey-white']};
      }
    }
  `;

  return (
    <div css={styles}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space size={16}>
          <StyledSelect
            bordered={false}
            value={pageSize}
            options={perPageList.map((item) => ({ label: item, value: item }))}
            onChange={(per_page) => onChange({ per_page, cursor: undefined })}
            popupMatchSelectWidth={false}
          />
          {t('resultsPerPage')}
        </Space>
        <Space size={8}>
          <StyledButton
            icon={<ChevronLeft fontSize={20} />}
            disabled={!previousCursor}
            onClick={() => previousCursor && onChange({ cursor: previousCursor, page: Math.max(1, currentPage - 1) })}
          />
          <span style={{ 
            color: currentTheme['grey-white'], 
            fontSize: '14px',
            fontWeight: '500',
            minWidth: '60px',
            textAlign: 'center'
          }}>
            {t('page')} {currentPage}
          </span>
          <StyledButton
            icon={<ChevronRight fontSize={20} />}
            disabled={!nextCursor}
            onClick={() => nextCursor && onChange({ cursor: nextCursor, page: currentPage + 1 })}
          />
        </Space>
      </Space>
    </div>
  );
};
